import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, AlertCircle, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TestTakingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [startTime] = useState(Date.now());
  const [result, setResult] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTest();
  }, [id]);

  useEffect(() => {
    if (test && test.time_limit_seconds) {
      setTimeRemaining(test.time_limit_seconds);
    }
  }, [test]);

  useEffect(() => {
    if (timeRemaining !== null && timeRemaining > 0 && !result) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleSubmit(true); // Auto-submit when time runs out
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [timeRemaining, result]);

  const loadTest = async () => {
    try {
      const response = await axios.get(`${API}/tests/${id}`, {
        withCredentials: true
      });
      
      // Also load the questions
      const questionsResponse = await axios.get(`${API}/tests/${id}/questions`, {
        withCredentials: true
      });

      let questions = questionsResponse.data;

      if (response.data.randomize_questions) {
        // Shuffle question order
        questions = shuffleArray(questions);

        // Shuffle each question's options while keeping the ability to
        // map the shuffled option index back to the original (canonical)
        // index, so grading against the backend stays correct.
        questions = questions.map(q => {
          const originalIndices = shuffleArray(q.options.map((_, idx) => idx));
          const shuffledOptions = originalIndices.map(origIdx => q.options[origIdx]);
          const newCorrectIndex = originalIndices.indexOf(q.correct_answer_index);
          return {
            ...q,
            options: shuffledOptions,
            correct_answer_index: newCorrectIndex,
            _originalIndices: originalIndices, // shuffledPosition -> originalPosition
          };
        });
      }

      // Attach questions to test object
      setTest({ ...response.data, questions });
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load test');
      navigate('/testing-center');
    } finally {
      setLoading(false);
    }
  };

  const shuffleArray = (arr) => {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  };

  const loadLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/tests/leaderboard/${id}`);
      setLeaderboard(response.data);
      setShowLeaderboard(true);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    }
  };

  const handleAnswer = (questionId, optionIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: optionIndex
    }));
  };

  const handleSubmit = async (autoSubmit = false) => {
    if (!autoSubmit) {
      // Check if all questions answered
      const allAnswered = test.questions.every(q => answers[q.id] !== undefined);
      if (!allAnswered && !window.confirm('You haven\'t answered all questions. Submit anyway?')) {
        return;
      }
    }

    const timeTaken = Math.floor((Date.now() - startTime) / 1000);

    // Translate shuffled option indices back to the backend's canonical
    // option order before submitting, so grading remains correct even
    // when questions/answers were mixed for this attempt.
    const canonicalAnswers = {};
    test.questions.forEach(q => {
      const selected = answers[q.id];
      if (selected !== undefined) {
        canonicalAnswers[q.id] = q._originalIndices ? q._originalIndices[selected] : selected;
      }
    });

    try {
      const response = await axios.post(
        `${API}/tests/${id}/submit`,
        { answers: canonicalAnswers, time_taken: timeTaken },
        { withCredentials: true }
      );
      
      setResult(response.data);
      toast.success(response.data.passed ? '🎉 You passed!' : 'Keep practicing!');
      
      // Load leaderboard after first attempt
      loadLeaderboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit test');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-[#D4A574] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!test) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="cassette-card p-12">
            <p className="text-[#3D2817] text-xl">Test not found</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="py-8 min-h-screen" data-testid="test-taking-page">
        <div className="max-w-4xl mx-auto px-4">
          
          {!result ? (
            /* Test Taking View */
            <>
              {/* Header with Timer */}
              <div className="cassette-card p-6 mb-6 sticky top-4 z-10">
                <div className="flex justify-between items-center">
                  <div>
                    <h1 className="text-3xl retro-title text-[#2C1810]">{test.title}</h1>
                    <p className="text-[#6B4423]">
                      {test.question_count} questions • {test.passing_score}% to pass
                    </p>
                  </div>
                  
                  {test.time_limit_seconds && (
                    <div className={`flex items-center gap-2 text-xl md:text-2xl font-bold ${
                      timeRemaining < 60 ? 'text-red-600' : 'text-[#E8934D]'
                    }`}>
                      <Clock className="w-6 h-6 md:w-8 md:h-8" />
                      <span>{formatTime(timeRemaining)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Warning about timer */}
              {test.time_limit_seconds && (
                <div className="cassette-card p-4 mb-6 bg-[#E8934D]/10 border-2 border-[#E8934D]">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-[#E85D4D] flex-shrink-0" />
                    <div>
                      <p className="font-bold text-[#2C1810]">Timer Active</p>
                      <p className="text-sm text-[#6B4423]">
                        Test will auto-submit when time runs out. Timer does NOT pause if you leave this page.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Sticky Timer Banner - Mobile Optimized */}
              {test.time_limit_seconds && !result && (
                <div className={`sticky top-0 z-50 mb-6 rounded-lg shadow-lg p-4 transition-colors ${
                  timeRemaining < 60 
                    ? 'bg-red-600 animate-pulse' 
                    : timeRemaining < 300 
                    ? 'bg-orange-500' 
                    : 'bg-[#4D9E7C]'
                }`}>
                  <div className="flex items-center justify-between text-white">
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 md:w-6 md:h-6" />
                      <span className="font-semibold text-sm md:text-base">Time Remaining</span>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold font-mono">
                      {formatTime(timeRemaining)}
                    </div>
                  </div>
                  {timeRemaining < 60 && (
                    <p className="text-white text-xs md:text-sm mt-2 text-center font-semibold">
                      ⚠️ Less than 1 minute remaining!
                    </p>
                  )}
                </div>
              )}

              {/* Question Navigation Bar */}
              <div className="cassette-card p-6 mb-6">
                <div className="flex flex-wrap gap-2">
                  {test.questions && test.questions.map((q, index) => (
                    <button
                      key={q.id}
                      onClick={() => setCurrentQuestionIndex(index)}
                      className={`w-12 h-12 rounded-lg font-bold transition-all ${
                        currentQuestionIndex === index
                          ? 'bg-[#E8934D] text-white scale-110'
                          : answers[q.id] !== undefined
                          ? 'bg-[#4D9E7C] text-white'
                          : 'bg-[#F4E4C1] text-[#2C1810] hover:bg-[#E8B67A]'
                      }`}
                      title={answers[q.id] !== undefined ? 'Answered' : 'Not answered'}
                      data-testid={`question-nav-button-${index}`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
                <p className="text-sm text-[#6B4423] mt-3">
                  Answered: {Object.keys(answers).length} / {test.questions?.length || 0}
                </p>
              </div>

              {/* Current Question */}
              {test.questions && test.questions[currentQuestionIndex] && (
                <div className="cassette-card p-8">
                  <p className="text-2xl font-bold text-[#2C1810] mb-6">
                    Question {currentQuestionIndex + 1} of {test.questions.length}
                  </p>
                  <p className="text-xl text-[#3D2817] mb-6">
                    {test.questions[currentQuestionIndex].question_text}
                  </p>
                  
                  <div className="space-y-3">
                    {test.questions[currentQuestionIndex].options && test.questions[currentQuestionIndex].options.map((option, oIndex) => (
                      <label
                        key={oIndex}
                        className={`flex items-center p-5 rounded-lg border-2 cursor-pointer transition-colors ${
                          answers[test.questions[currentQuestionIndex].id] === oIndex
                            ? 'border-[#E8934D] bg-[#E8934D]/10 border-4'
                            : 'border-[#D4A574] hover:bg-[#F4E4C1]'
                        }`}
                        data-testid={`answer-option-${oIndex}`}
                      >
                        <input
                          type="radio"
                          name={`question-${test.questions[currentQuestionIndex].id}`}
                          checked={answers[test.questions[currentQuestionIndex].id] === oIndex}
                          onChange={() => handleAnswer(test.questions[currentQuestionIndex].id, oIndex)}
                          className="mr-4 w-5 h-5"
                          data-testid={`answer-option-input-${oIndex}`}
                        />
                        <span className="text-lg text-[#3D2817] font-medium">
                          {String.fromCharCode(65 + oIndex)}. {option}
                        </span>
                      </label>
                    ))}
                  </div>

                  {/* Navigation Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 mt-8">
                    {currentQuestionIndex > 0 && (
                      <button
                        onClick={() => setCurrentQuestionIndex(currentQuestionIndex - 1)}
                        className="retro-button-small flex-1"
                      >
                        ← Previous
                      </button>
                    )}
                    {currentQuestionIndex < test.questions.length - 1 && (
                      <button
                        onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                        className="retro-button-small flex-1"
                      >
                        Next →
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="sticky bottom-4 mt-8">
                <button
                  onClick={() => handleSubmit(false)}
                  className="retro-button w-full text-xl py-4"
                  data-testid="submit-test-button"
                >
                  Submit Test
                </button>
              </div>
            </>
          ) : (
            /* Results View */
            <div className="space-y-6">
              {/* Score Card */}
              <div className={`cassette-card p-8 text-center ${
                result.passed ? 'border-4 border-[#4D9E7C]' : 'border-4 border-[#E85D4D]'
              }`}>
                <div className="text-6xl mb-4">{result.passed ? '🎉' : '📚'}</div>
                <h2 className="text-4xl retro-title mb-4">
                  {result.passed ? 'Congratulations!' : 'Keep Practicing!'}
                </h2>
                <p className="text-6xl font-bold text-[#E8934D] mb-4">{result.score.toFixed(1)}%</p>
                <p className="text-xl text-[#6B4423] mb-2">
                  {result.correct_count} out of {result.total_questions} correct
                </p>
                <p className="text-[#6B4423]">
                  Time taken: {formatTime(result.time_taken)}
                </p>
                <p className="text-[#6B4423]">
                  Passing score: {result.passing_score}%
                </p>
              </div>

              {/* Correct Answers */}
              <div className="cassette-card p-6">
                <h3 className="text-2xl retro-title text-[#2C1810] mb-4">Review Answers</h3>
                <div className="space-y-4">
                  {test.questions && test.questions.map((question, qIndex) => {
                    const userAnswer = answers[question.id];
                    const isCorrect = userAnswer === question.correct_answer_index;
                    
                    return (
                      <div key={question.id} className={`p-4 rounded-lg ${
                        isCorrect ? 'bg-[#4D9E7C]/10' : 'bg-[#E85D4D]/10'
                      }`}>
                        <p className="font-bold text-[#2C1810] mb-2">
                          {qIndex + 1}. {question.question_text}
                        </p>
                        <p className={`mb-1 ${isCorrect ? 'text-[#4D9E7C]' : 'text-[#E85D4D]'}`}>
                          Your answer: {question.options[userAnswer] || 'Not answered'} {isCorrect ? '✓' : '✗'}
                        </p>
                        {!isCorrect && (
                          <p className="text-[#4D9E7C]">
                            Correct answer: {question.options[question.correct_answer_index]}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Leaderboard */}
              {showLeaderboard && leaderboard.length > 0 && (
                <div className="cassette-card p-6">
                  <h3 className="text-2xl retro-title text-[#2C1810] mb-4 flex items-center gap-2">
                    <TrendingUp className="w-6 h-6" />
                    Leaderboard (First Attempts)
                  </h3>
                  <div className="space-y-2">
                    {leaderboard.map((attempt, index) => (
                      <div key={attempt.id} className="flex justify-between items-center p-3 bg-white rounded">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl font-bold text-[#E8934D]">#{index + 1}</span>
                          <span className="text-[#2C1810] font-semibold">{attempt.user_name}</span>
                        </div>
                        <span className="text-[#E8934D] font-bold">{attempt.score.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/testing-center')}
                  className="retro-button flex-1"
                >
                  Back to Testing Center
                </button>
                <button
                  onClick={() => navigate('/my-learning')}
                  className="retro-button flex-1"
                >
                  View My Progress
                </button>
              </div>
            </div>
          )}

        </div>
      </section>
    </Layout>
  );
}
